import { createResourceEntityAdapter } from 'app-components/flux/core/resourceSliceUtils';
import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import {
  mkScheduledTransactionPatch,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { getLogger, tracker } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import billPresentmentAPI from './billPresentmentAPI';
// forward selectors from file
import {
  getCanBVA,
  getHasBVA,
  getIsPotentialExpense,
  getShowBillTab,
  getShowConnectOption,
} from './billPresentmentSelectors';
import type {
  Biller,
  BillerAccount,
  BillPresentmentSlice,
  EnrollmentErrorPayload,
  SubmitRequest,
} from './billPresentmentTypes';
import { BILL_PRESENTMENT_REDUCER_KEY } from './billPresentmentTypes';
import {
  canLinkBiller,
  getEnrollmentErrorMessage,
  toEnrollmentErrorPayload,
  trackBillerAccountChanges,
} from './billPresentmentUtils';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const initialState: BillPresentmentSlice = {
  billers: {
    entities: {},
    ids: [],
  },
  billerAccounts: {
    entities: {},
    ids: [],
  },
  // these all refer to billerAccounts
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};

const log = getLogger('app-components/flux/bill-presentment/slice');

const billerAdapter = createResourceEntityAdapter<Biller>();
export const {
  selectAll: selectAllBillers,
  selectById: selectBillerById,
  // …others available like selectIds, selectEntities, selectTotal
} = billerAdapter.getSelectors((state: BillPresentmentSlice) => state.billers);

const billerAccountAdapter = createResourceEntityAdapter<BillerAccount>();
export const {
  selectAll: selectAllBillerAccounts,
  selectById: selectBillerAccountById,
  // …others available like selectIds, selectEntities, selectTotal
} = billerAccountAdapter.getSelectors((state: BillPresentmentSlice) => state.billerAccounts);

export const slice = createAppSlice({
  name: BILL_PRESENTMENT_REDUCER_KEY,
  initialState: billerAccountAdapter.getInitialState(initialState) as BillPresentmentSlice,
  reducers: (create) => ({
    // GET BillerAccounts
    fetch: create.asyncThunk(
      async (_: void, thunkApi) => {
        log?.debug('Getting billerAccounts');
        const billerAccounts = await billPresentmentAPI.fetch();
        const billerIds = billerAccounts.map((acc) => acc.billerId);
        if (billerIds?.length) thunkApi.dispatch(slice.actions.getBillers(billerIds));
        return billerAccounts;
      },
      {
        pending(state) {
          state.isLoading = true;
        },
        fulfilled(state, action) {
          log?.debug('Getting billerAccounts, response', action.payload);
          trackBillerAccountChanges(state, action.payload);
          billerAccountAdapter.setAll(state.billerAccounts, action.payload);
          state.error = undefined;
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
        settled(state) {
          state.isLoading = false;
          state.loadPending = false;
        },
      },
    ),
    getBillerAccount: create.asyncThunk(
      async (id: string, thunkApi) => {
        const account = await billPresentmentAPI.getBillerAccount(id);
        if (account.billerId) thunkApi.dispatch(slice.actions.getBiller(account.billerId));
        return account;
      },
      {
        fulfilled(state, action) {
          billerAccountAdapter.setOne(state.billerAccounts, action.payload);
        },
      },
    ),
    // DELETE/de-enroll BillerAccount
    disconnect: create.asyncThunk(
      async ({ model, hideUndo }: { model: ScheduledTransactionType; hideUndo?: boolean }, thunkApi) => {
        // receives ST model as argument
        log?.debug('Deleting BillerAccount from model:', model);
        try {
          const id = model?.presentmentAccountId;
          try {
            if (id) await billPresentmentAPI.delete(id);
          } catch {
            if (slice?.actions?.fetch) thunkApi.dispatch(slice.actions.fetch());
          }
          if (id || model?.billPresentmentAccountId) {
            thunkApi.dispatch(
              scheduledTransactionsActions.updateScheduledTransaction(
                mkScheduledTransactionPatch({
                  id: model.id,
                  billPresentmentAccountId: '', // clear old BillGoField
                  presentmentAccountId: '', // clear new fiServ field
                  // QCS clears these along with presentmentAccountId; mirror that locally
                  billerEnrollmentStatus: null,
                  billerType: null,
                  billSnapshot: null,
                }),
                hideUndo ? {} : { undo: { userMessage: 'Bill Disconnected', disableUndoAction: true } },
              ),
            );
          }
        } catch (e) {
          thunkApi.dispatch(scheduledTransactionsActions.getScheduledTransactions());
          throw e;
        }
      },
      {
        pending(state, action) {
          state.isLoading = true;
          const { model } = action.meta.arg;
          if (model?.presentmentAccountId) {
            billerAccountAdapter.removeOne(state.billerAccounts, model.presentmentAccountId);
          }
        },
        fulfilled() {
          log?.debug('Delete billerAccount successful');
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
        settled(state) {
          state.isLoading = false;
          state.loadPending = false;
        },
      },
    ),
    // GET Biller
    getBiller: create.asyncThunk(
      async (id: string) => {
        log?.debug('Getting biller', id);
        try {
          return await billPresentmentAPI.getBiller(id);
        } catch (e) {
          log.warn('error retriving biller:', id);
          throw e;
        }
      },
      {
        fulfilled(state, action) {
          log?.debug('Get biller successful', action.payload);
          billerAdapter.addOne(state.billers, action.payload);
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
      },
    ),
    // BULK GET Billers
    getBillers: create.asyncThunk(
      async (ids: string[]) => {
        log?.debug('Getting billers');
        try {
          return await billPresentmentAPI.getBillers(ids);
        } catch (e) {
          log.warn('error retriving billers:', e);
          throw e;
        }
      },
      {
        fulfilled(state, action) {
          log?.debug('Get biller successful', action.payload);
          billerAdapter.setMany(state.billers, action.payload);
        },
        rejected(state, action) {
          log?.error('Received error', action.error);
          state.error = action.error;
        },
      },
    ),
    // ENROLLMENT
    doEnrollment: create.asyncThunk(
      async (
        { payload, series, biller }: { payload: SubmitRequest; series: ScheduledTransactionType; biller: Biller },
        thunkApi,
      ) => {
        const type = biller?.activation === 'REAL_TIME' ? 'fiservRealTime' : 'fiservBatch';
        let enrollmentStage: 'conflictCheck' | 'submit' | 'confirm' | 'statusCheck' = 'conflictCheck';
        // Stop before enrolling anything: without a series id the biller would be
        // enrolled but never linked (SIMPL-30330).
        if (!canLinkBiller(series)) {
          const errorPayload: EnrollmentErrorPayload = {
            title: 'Series not saved',
            detail: 'Save this series, then connect your biller.',
          };
          log.error('doEnrollment called for a series with no id', { billerId: biller?.id });
          tracker.track(tracker.events.fiservBillFailed, {
            billerName: biller?.name,
            type,
            stage: 'seriesCheck',
            reasonDescription: getEnrollmentErrorMessage(errorPayload),
          });
          return thunkApi.rejectWithValue(errorPayload);
        }
        try {
          const accountNumber = payload.data.parameters
            .find((param) => param.name === 'account-number')
            ?.fields?.find((field) => field.name === 'account-number')?.value;
          if (accountNumber) {
            const conflictResponse = await billPresentmentAPI.checkConflicts(payload.billerId, accountNumber);
            if (conflictResponse?.id) {
              // orphaned biller account exists
              if (conflictResponse.status === 'CONNECTED') {
                // connected account already exists - just add to model
                thunkApi.dispatch(
                  scheduledTransactionsActions.updateScheduledTransaction(
                    mkScheduledTransactionPatch({
                      id: series?.id,
                      presentmentAccountId: conflictResponse.id,
                      autoAdjustAmount: 'EBILL_LAST_STATEMENT_BALANCE',
                    }),
                  ),
                );
                tracker.track(tracker.events.fiservBillSuccess, { billerName: biller?.name, type: biller?.activation });
                return conflictResponse;
              } else {
                // old biller account exists elsewhere and needs to be deleted
                if (conflictResponse.id !== series?.presentmentAccountId) {
                  await billPresentmentAPI.delete(conflictResponse.id);
                }
              }
            }
          } else {
            log.warn('NO AccountNumber found for FiServ payload!');
          }

          if (series?.presentmentAccountId) {
            // delete and remove old account on reconnect
            await thunkApi.dispatch(slice.actions.disconnect({ model: series, hideUndo: true }));
          }

          enrollmentStage = 'submit';
          const submitResponse = await billPresentmentAPI.submit(payload);
          log.debug('Submit response', submitResponse);

          if (submitResponse.status === 'SUBMITTED' && submitResponse.id) {
            log.debug('Submit successful!', submitResponse);

            enrollmentStage = 'confirm';
            const confirmResponse = await billPresentmentAPI.confirm(submitResponse.id);
            log.debug('Confirm response', confirmResponse);

            if (confirmResponse.status === 'CONNECTED' || confirmResponse.status === 'PENDING') {
              log.debug('Confirm successful!', confirmResponse);

              thunkApi.dispatch(
                scheduledTransactionsActions.updateScheduledTransaction(
                  mkScheduledTransactionPatch({
                    id: series?.id,
                    presentmentAccountId: confirmResponse.id,
                    autoAdjustAmount: 'EBILL_LAST_STATEMENT_BALANCE',
                  }),
                ),
              );
            }

            tracker.track(tracker.events.fiservBillPending, {
              billerName: biller?.name,
              type,
            });
            enrollmentStage = 'statusCheck';
            log.debug('Waiting before get...');
            if (biller?.activation === 'REAL_TIME') {
              // wait longer for real-time as there is a greater chance it could actually connect
              await delay(15000);
            } else {
              await delay(12000);
            }
            const updatedBillerAccount: BillerAccount = await thunkApi
              .dispatch(slice.actions.getBillerAccount(confirmResponse.id))
              .unwrap();
            if (!updatedBillerAccount) return;
            if (updatedBillerAccount.status !== 'PENDING') {
              tracker.track(tracker.events.fiservBillStatusChange, {
                billerName: biller?.name,
                type,
                billerId: updatedBillerAccount.billerId,
                billerAccountId: updatedBillerAccount.id,
                oldStatus: 'PENDING',
                newStatus: updatedBillerAccount.status,
                reasonCode: updatedBillerAccount.additionalInfo?.code,
                reasonDescription: updatedBillerAccount.additionalInfo?.description,
                reasonErrorCode: updatedBillerAccount.additionalInfo?.details?.[0]?.errorCode,
                reasonDetails: updatedBillerAccount.additionalInfo?.details,
              });
            }
            if (updatedBillerAccount.status === 'CONNECTED') {
              tracker.track(tracker.events.fiservBillSuccess, {
                billerName: biller?.name,
                type,
              });
            } else if (updatedBillerAccount.status === 'FAILED' || updatedBillerAccount.status === 'DISCONNECTED') {
              tracker.track(tracker.events.fiservBillFailed, {
                billerName: biller?.name,
                type,
                reasonCode: updatedBillerAccount.additionalInfo?.code,
                reasonDescription: updatedBillerAccount.additionalInfo?.description,
                reasonErrorCode: updatedBillerAccount.additionalInfo?.details?.[0]?.errorCode,
                reasonDetails: updatedBillerAccount.additionalInfo?.details,
              });
            }
            return updatedBillerAccount;
          }
          return;
        } catch (e) {
          log.error('Enrollment failed', e);
          const errorPayload = toEnrollmentErrorPayload(e);
          tracker.track(tracker.events.fiservBillFailed, {
            billerName: biller?.name,
            type,
            stage: enrollmentStage,
            reasonCode: errorPayload.code,
            reasonDescription: getEnrollmentErrorMessage(errorPayload),
            reasonErrorCode: errorPayload.errorCode,
            reasonDetails: errorPayload.fieldErrors,
          });
          return thunkApi.rejectWithValue(errorPayload);
        }
      },
    ),
  }),
  selectors: {
    getLoadPending: (s) => s.loadPending,
    getIsLoading: (s) => s.isLoading,
    getError: (s) => s.error,
    selectAllBillers,
    selectBillerById,
    selectAllBillerAccounts,
    selectBillerAccountById,
    selectBillerForAccountId: (state, id?: string) => {
      const acc = id && selectBillerAccountById(state, id);
      return acc ? selectBillerById(state, acc.billerId) : null;
    },
  },
});

export const billPresentmentActions = slice.actions;
export const billPresentmentSelectors = {
  ...slice.selectors,
  getCanBVA,
  getHasBVA,
  getIsPotentialExpense,
  getShowConnectOption,
  getShowBillTab,
};
export { BILL_PRESENTMENT_REDUCER_KEY };

export default slice;
